import React, { useEffect } from 'react';
import PropTypes from 'prop-types';
import { focusElement, scrollToTop } from 'platform/utilities/ui';
import FormTitle from 'platform/forms-system/src/js/components/FormTitle';
import SaveInProgressIntro from 'platform/forms/save-in-progress/SaveInProgressIntro';
import { useSelector } from 'react-redux';
import { isLoggedIn } from 'platform/user/selectors';
import { TITLE, SUBTITLE } from '../constants';

// Components
import OmbInfo from '../components/OmbInfo';
import TechnologyProgramAccordion from '../components/TechnologyProgramAccordion';

const customLink = ({ children, ...props }) => {
  return (
    <va-link-action
      type="primary-entry"
      text="Start your request for reimbursement"
      {...props}
    >
      {children}
    </va-link-action>
  );
};

const ProcessList = () => {
  return (
    <va-process-list>
      <va-process-list-item header="Check your eligibility">
        <p>
          To receive reimbursement for a preparatory course, you’ll need to
          apply and be found eligible for VA education benefits under Chapter 33
          (including TOE and Fry) or Chapter 35.
        </p>
      </va-process-list-item>
      <va-process-list-item header="Gather your information">
        <p className="vads-u-font-size--sans-md vads-u-font-weight--bold">
          Here’s what you’ll need to apply:
        </p>
        <ul>
          <li>
            Your social security or VA file number and if applicable, your payee
            number
          </li>
          <li>Your current mailing address and contact information</li>
          <li>The name of licensing or certification test</li>
          <li>
            The name and address of the organization issuing the license or
            certification
          </li>
          <li>
            The receipt that lists the prep course costs including any mandatory
            fees
          </li>
          <li>
            A copy of your verification of enrollment from your prep course
            provider
          </li>
          <li>
            The provider verification must be provided on official letterhead
            and include the course name and the start and end dates of the prep
            course
          </li>
        </ul>
        <p>If you don’t already have them, gather them now.</p>
      </va-process-list-item>
      <va-process-list-item header="Fill out the online form">
        <p>
          We’ll take you through each step of the process. It should take about
          15 minutes. You’ll also be provided the opportunity to give additional
          commentary regarding your preparatory course.
        </p>
        <p>
          <strong>Note:</strong> When you fill out your form, you will be asked
          for your address. When you update your address, your VA Profile
          address will also update.
        </p>
      </va-process-list-item>
      <va-process-list-item header="Submit the online form">
        <p>
          Your completed form will submit automatically once you are done
          filling out information about your prep course and uploading your
          supporting documents.
        </p>
      </va-process-list-item>
    </va-process-list>
  );
};

export const IntroductionPage = props => {
  const userLoggedIn = useSelector(state => isLoggedIn(state));
  const { route } = props;
  const { formConfig, pageList } = route;

  useEffect(() => {
    scrollToTop();
    focusElement('h1');
  }, []);

  return (
    <article className="schemaform-intro">
      <FormTitle title={TITLE} subTitle={SUBTITLE} />

      <p className="vads-u-font-size--lg vads-u-color--base vads-u-font-weight--normal">
        VA Form 22-10272 is used to request reimbursement for prep courses that
        help you prepare for licensing or certification tests.
      </p>

      <h2 className="vad-u-margin-top--0">
        Follow these steps to get started:
      </h2>

      <ProcessList />

      <div className="vads-u-margin-bottom--4">
        <va-additional-info trigger="What happens after you apply">
          <p>
            After you successfully submit your form, we will review your
            documents. You should hear back within 30 days about your
            reimbursement.
          </p>
        </va-additional-info>
      </div>

      <SaveInProgressIntro
        hideUnauthedStartLink={!userLoggedIn}
        headingLevel={2}
        prefillEnabled={formConfig.prefillEnabled}
        messages={formConfig.savedFormMessages}
        formConfig={formConfig}
        pageList={pageList}
        unauthStartText="Sign in or create an account"
        customLink={userLoggedIn ? customLink : null}
      />
      <p />

      <div
        className={userLoggedIn ? 'vads-u-margin-top--4' : ''}
        data-testid="omb-info"
      >
        <OmbInfo />
      </div>
      <TechnologyProgramAccordion />
    </article>
  );
};

IntroductionPage.propTypes = {
  route: PropTypes.shape({
    formConfig: PropTypes.shape({
      prefillEnabled: PropTypes.bool.isRequired,
      savedFormMessages: PropTypes.object.isRequired,
    }).isRequired,
    pageList: PropTypes.arrayOf(PropTypes.object).isRequired,
  }).isRequired,
  location: PropTypes.shape({
    basename: PropTypes.string,
  }),
};

export default IntroductionPage;
